import { useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CenteredSpinner } from '@/components/CenteredSpinner';
import { TIMEOUTS } from '@/constants';
import { ACTIVE_STUDY_STATUSES } from './-data/constants';
import StudyDashboard from './-components/dashboard/StudyDashboard';
import {
  flattenStudyPages,
  studyListInfiniteOptions,
  studyListLoader,
} from './-data/study-list';

function SimOpsDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const studiesQuery = useInfiniteQuery({
    ...studyListInfiniteOptions(),
    refetchInterval: (query) => {
      const items = flattenStudyPages(query.state.data);
      return items.some((s) => ACTIVE_STUDY_STATUSES.has(s.status))
        ? TIMEOUTS.STUDY_POLL_INTERVAL
        : false;
    },
  });

  const studies = useMemo(
    () => flattenStudyPages(studiesQuery.data),
    [studiesQuery.data],
  );
  const handleOpenStudy = (studyId: string) => {
    // Terminal studies open on `/results` so the merged header renders
    // its Overview / Design Points segmented control + Writeup + Export
    // PDF (ENG-1842 / ENG-1843). Failed runs can have saved diagnostics.
    // Any other status keeps the bare URL,
    // where `$studyId/index.tsx` dispatches the phase view on `status`.
    const study = studies.find((s) => s.id === studyId);
    const hasTerminalResults =
      study?.status === 'completed' ||
      study?.status === 'completed_with_issues' ||
      study?.status === 'failed';
    void navigate({
      to: hasTerminalResults ? '/simops/$studyId/results' : '/simops/$studyId',
      params: { studyId },
    });
  };

  const handleNewStudy = () => {
    void navigate({ to: '/simops/new' });
  };

  if (studiesQuery.isLoading) {
    return <CenteredSpinner />;
  }

  return (
    <div className="h-full flex-1 overflow-hidden">
      <StudyDashboard
        studies={studies}
        library="simops"
        error={studiesQuery.isError ? t('simopsRoutes.list.loadError') : null}
        onOpenStudy={handleOpenStudy}
        onNewStudy={handleNewStudy}
        hasNextPage={studiesQuery.hasNextPage}
        isFetchingNextPage={studiesQuery.isFetchingNextPage}
        onLoadMore={() => void studiesQuery.fetchNextPage()}
      />
    </div>
  );
}

export const Route = createFileRoute('/_app/simops/')({
  // Shared with `/evaltool` — see `studyListLoader`. Active-study polling stays
  // component-level.
  loader: ({ context: { queryClient } }) => studyListLoader(queryClient),
  component: SimOpsDashboardPage,
});
